import { SIGN_FORM_STEP_HEADING_FOCUS_TARGET } from '#src/app/pages/petitionDetails/shared/consts';
import type { FocusFallbackListener } from '#src/app/shared/hooks/focus';

/**
 * Focus fallback for the two-step sign form. When the continue button is removed (because the
 * supporter just advanced from step 1 to step 2), focus would otherwise escape to the app-wide
 * fallback in App.tsx, which focuses the page's `[data-maincontent-principal]` wrapper — the
 * petition title and description. Instead, focus lands on the step 2 heading.
 *
 * The heading is rendered by the surrounding surface rather than by the form itself, so it is
 * resolved two ways:
 * - the desktop sidebar and the embedded form wrap their own heading, which stamps
 *   `data-focus-target="sign-form-step-heading"`;
 * - the mobile modal renders its heading in `<ModalHeader>`, a sibling of `<ModalBody>` inside
 *   react-aria's `<section role="dialog">`, so it is reached via the dialog's `h2[slot="title"]`.
 *
 * If neither is available, focus falls back to the container so it stays inside the form.
 */
export const signFormStepFocusFallback: FocusFallbackListener = ({ container, focus }) => {
	const heading =
		container.querySelector<HTMLElement>(`[data-focus-target="${SIGN_FORM_STEP_HEADING_FOCUS_TARGET}"]`) ??
		container.closest('[role="dialog"]')?.querySelector<HTMLElement>('h2[slot="title"]');

	return focus(heading ?? container);
};
